import { memo, type ReactNode, useState, useRef, useCallback, useEffect } from 'react';
import type { Spectrum } from '@bangos/shared';
import { useT, useTranslateSpectrum } from '../i18n';
import '../styles/gameplay.css';

interface GuessingSliderProps {
  spectrum: Spectrum;
  disabled: boolean;
  onConfirm: (position: number) => void;
  /** Initial position to display (e.g., if re-rendering after a guess was submitted). */
  initialPosition?: number;
  /** Whether a guess has already been submitted by this player. */
  hasGuessed?: boolean;
}

export const GuessingSlider = memo(function GuessingSlider({
  spectrum,
  disabled,
  onConfirm,
  initialPosition = 50,
  hasGuessed = false,
}: GuessingSliderProps): ReactNode {
  const t = useT();
  const tSpectrum = useTranslateSpectrum();
  const spectrumLabels = tSpectrum(spectrum);
  const [position, setPosition] = useState<number>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  // Ref to hold the latest position during drag, updated in rAF
  const draggingPositionRef = useRef<number>(initialPosition);
  // Ref to track if an rAF callback is pending
  const rafRef = useRef<number | null>(null);

  // Reset position when initialPosition changes (e.g., new round)
  useEffect(() => {
    setPosition(initialPosition);
    draggingPositionRef.current = initialPosition;
  }, [initialPosition]);

  const getPositionFromClientX = useCallback((clientX: number): number => {
    const track = trackRef.current;
    if (!track) {
      console.warn('GuessingSlider: trackRef is null, skipping getBoundingClientRect');
      return position;
    }
    const rect = track.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    return Math.round((x / rect.width) * 100);
  }, [position]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled || hasGuessed) return;
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setIsDragging(true);
    const newPos = getPositionFromClientX(e.clientX);
    draggingPositionRef.current = newPos;
    setPosition(newPos);
  }, [disabled, hasGuessed, getPositionFromClientX]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const newPos = getPositionFromClientX(e.clientX);
    draggingPositionRef.current = newPos;
    // Use rAF to throttle React state updates to once per animation frame
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setPosition(draggingPositionRef.current);
      });
    }
  }, [isDragging, getPositionFromClientX]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    setIsDragging(false);
    // Cancel any pending rAF and apply the final position
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const newPos = getPositionFromClientX(e.clientX);
    draggingPositionRef.current = newPos;
    setPosition(newPos);
  }, [isDragging, getPositionFromClientX]);

  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (disabled || hasGuessed) return;
    // Only handle clicks that aren't part of a drag
    if (isDragging) return;
    const newPos = getPositionFromClientX(e.clientX);
    setPosition(newPos);
  }, [disabled, hasGuessed, isDragging, getPositionFromClientX]);

  const handleConfirm = useCallback(() => {
    if (disabled || hasGuessed) return;
    onConfirm(position);
  }, [disabled, hasGuessed, onConfirm, position]);

  const markerLeft = `${position}%`;

  const markerClass = [
    'guess-marker',
    isDragging ? 'guess-marker--active' : '',
    disabled ? 'guess-marker--disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="guess-container">
      <div className="guess-spectrum-labels">
        <span className="guess-label--left">{spectrumLabels.left}</span>
        <span className="guess-label--right">{spectrumLabels.right}</span>
      </div>

      <div
        className="guess-track-wrapper"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div
          ref={trackRef}
          className="guess-track"
          onClick={handleTrackClick}
        >
          <div
            className={markerClass}
            style={{ left: markerLeft }}
          />
        </div>
      </div>

      <div className="guess-position-label">
        {t('guessingSlider.position')}: {position}
      </div>

      {hasGuessed ? (
        <div className="guess-badge">
          {t('guessingSlider.guessSubmitted', { position })}
        </div>
      ) : (
        <button
          className="guess-confirm-btn"
          onClick={handleConfirm}
          disabled={disabled}
        >
          {disabled ? t('guessingSlider.waiting') : t('guessingSlider.confirmGuess')}
        </button>
      )}
    </div>
  );
});
